import React, {
	useCallback,
	useState,
	useEffect,
	type FC,
	type KeyboardEvent,
	useRef,
} from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AIContainer, AiIcon as AILogo } from '@atlassian/jira-atlassian-intelligence';
import { useIntl } from '@atlassian/jira-intl';
import type { ChildIssueType } from '@atlassian/jira-issue-view-common-types/src/child-issue-type';
import { smoothScrollIntoCenterIfNeeded } from '@atlassian/jira-issue-view-common-utils';
import { IssueBreakdownSkeleton } from '@atlassian/jira-skeletons';
import messages from '../../messages';
import { FeedbackForm } from '../common/feedback-form';
import { Header } from '../common/header';
import { IssueTypeOptions } from '../common/issue-type-options';

type Props = {
	onSubmit(issueTypeIds: string[]): void;
	onCancel(): void;
	isLoading: boolean;
	headerPromptValue?: string;
	childIssueTypes: ChildIssueType[];
	issueHierarchyLevel?: number | undefined;
};

export const IssueBreakdownPrompt: FC<Props> = ({
	onSubmit,
	onCancel,
	isLoading,
	childIssueTypes,
	issueHierarchyLevel,
}) => {
	const [hasSubmitted, setHasSubmitted] = useState(false);

	const { formatMessage } = useIntl();

	const onIssueTypeButtonClick = useCallback(
		(issueType: ChildIssueType) => {
			onSubmit([issueType.id]);
			setHasSubmitted(true);
		},

		[onSubmit],
	);

	// Necessary for scrolling now that input is no longer on prompt step
	const fieldRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const { current } = fieldRef;
		if (current) {
			smoothScrollIntoCenterIfNeeded(current);
			current.focus({ preventScroll: true });
		}
	}, []);
	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation(); // Prevents issue modal from closing
				onCancel();
			}
		},
		[onCancel],
	);

	return (
		<Box ref={fieldRef} xcss={promptContainerStyles} tabIndex={-1} onKeyDown={handleKeyDown}>
			<AIContainer
				shouldFitContainer
				isShadow
				spacing="none"
				isLoading={isLoading}
				testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.ai-container"
			>
				{isLoading && (
					<>
						<Header issueHierarchyLevel={issueHierarchyLevel} />
						<IssueBreakdownSkeleton />
					</>
				)}
				{!isLoading && (
					<IssueTypeOptions
						childIssueTypes={childIssueTypes}
						onIssueTypeButtonClick={onIssueTypeButtonClick}
					/>
				)}

				<FooterSection hasBorder={isLoading}>
					{isLoading && (
						<>
							<AILogo size="medium" label="" />
							<FooterText data-testid="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.issue-breakdown-footer-brand-text">
								{formatMessage(messages.footerTextIsWorking)}
							</FooterText>
						</>
					)}
					<FooterSectionSpacer />
					<Button
						testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.cancel-esc-button"
						type="button"
						onClick={onCancel}
						interactionName="auto-generated.issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.cancel-esc-button.button"
					>
						{!isLoading ? formatMessage(messages.labelCancel) : formatMessage(messages.labelStop)}
						<EscText>{formatMessage(messages.labelEscNonFinal)}</EscText>
					</Button>
				</FooterSection>

				<Inline xcss={footerStyle} spread="space-between" alignBlock="center" space="space.050">
					<FooterSectionSpacer />
					<FooterText data-testid="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.issue-breakdown-footer-brand-text">
						{formatMessage(messages.footerTextPromptNonFinal)}
					</FooterText>
					<AILogo isDisabled size="small" label="" />
				</Inline>

				{/* User should be able to give feedback if they select an issue type option but there's no suggested issues returned (i.e. when an error is shown) */}
				{hasSubmitted && !isLoading && <FeedbackForm />}
			</AIContainer>
		</Box>
	);
};

const promptContainerStyles = xcss({
	marginTop: 'space.200',
	marginBottom: 'space.200',
	border: 0,
	outline: 'none', // remove focus highlight
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterText = styled.div({
	color: token('color.text.subtlest', '#626F86'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterSection = styled.div<{ hasBorder?: boolean }>(
	{
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		padding: token('space.150', '12px'),
		gap: token('space.150', '12px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasBorder = false }) =>
		hasBorder
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					borderTop: `1px solid ${token('color.background.accent.gray.subtler', colors.N40)}`,
				}
			: {},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterSectionSpacer = styled.div({
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EscText = styled.span({
	borderRadius: token('border.radius.100', '3px'),
	padding: token('space.050', '4px'),
	color: token('color.text.subtle', '#44546F'),
	background: token('color.background.neutral', '#091E420F'),
	fontWeight: '400',
	marginLeft: token('space.075', '6px'),
});

const footerStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.border', colors.N40)}`,
	paddingInline: 'space.150',
	paddingBlock: 'space.150',
});
